import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { commentsApi, type MentionUser } from '../api/commentsApi';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
// Stable reference (not a fresh `[]` per render) so consumers can safely
// depend on `results` in a useEffect without it "changing" on every render.
const EMPTY_RESULTS: MentionUser[] = [];

/**
 * Debounced Techsell-user search for the `@` mention picker. Unlike useUserSearch.ts,
 * `query` is owned by the caller — this hook only debounces and fetches it.
 */
export const useMentionableUsers = (issueKey: string, query: string) => {
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  const enabled = !!issueKey && debounced.length >= MIN_CHARS;

  const { data, isFetching, error } = useQuery({
    queryKey: ['mentionable-users', issueKey, debounced],
    queryFn: () => commentsApi.searchMentionableUsers(issueKey, debounced),
    enabled,
    staleTime: 30_000,
  });

  const results: MentionUser[] = enabled ? data ?? EMPTY_RESULTS : EMPTY_RESULTS;

  return {
    results,
    loading: enabled && isFetching,
    error: error instanceof Error ? error.message : null,
    hasEnoughChars: enabled,
    minChars: MIN_CHARS,
  };
};
